import React, { useEffect, useRef } from "react";
import Box from "@mui/material/Box";

const boxStyle = {
  width: 52,
  height: 52,
  borderRadius: "10px",
  border: "1.5px solid",
  borderColor: "divider",
  textAlign: "center" as const,
  fontWeight: 700,
  fontSize: 20,
  fontFamily: "inherit",
  outline: "none",
  transition: "border-color 0.15s",
  "&:focus": {
    borderColor: "primary.main",
    boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
  },
};

export default function OtpInput({
  length = 6,
  value,
  onChange,
}: {
  length?: number;
  value: string;
  onChange: (v: string) => void;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    refs.current = refs.current.slice(0, length);
  }, [length]);

  const chars = Array.from({ length }, (_, i) => value[i] || "");

  const setAt = (i: number, c: string) => {
    const next = value.split("");
    next[i] = c;
    onChange(next.join("").slice(0, length));
  };

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!chars[i] && i > 0) refs.current[i - 1]?.focus();
      setAt(i, "");
      e.preventDefault();
    }
    if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < length - 1) refs.current[i + 1]?.focus();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const t = e.clipboardData.getData("text").replace(/\s/g, "").slice(0, length);
    if (t) onChange(t);
    e.preventDefault();
  };

  return (
    <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", my: 2 }}>
      {chars.map((c, i) => (
        <Box
          key={i}
          component="input"
          ref={(el: HTMLInputElement | null) => {
            refs.current[i] = el;
          }}
          inputMode="numeric"
          maxLength={1}
          value={c}
          onPaste={onPaste}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => onKeyDown(i, e)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 1);
            setAt(i, v);
            if (v && i < length - 1) refs.current[i + 1]?.focus();
          }}
          sx={boxStyle}
        />
      ))}
    </Box>
  );
}
